import { ConversionCopy } from "../pages/[region]/[lang]/product/[slug]";
import { getCollectionPreview } from "./algolia/collection-preview";
import sanityClient from "./sanity-client";
import { ShippingCopy } from "./sanity-schema";
import { shippingCopyQuery } from "./sanity/product-queries";
import { DereferencedTabbedCollection } from "./sanity/tabbed-collection-queries";
import {
  buildTranslationGroupQuery,
  toCopyObject,
  TranslationGroupSanityType,
} from "./sanity/translation-group-queries";
import { ShopCardPropsWithSlug } from "./transformers/shop-card";
import { Dialect } from "./utils/locale";

/** A Tabbed Collection after {@link resolveTabbedCollections} has run: every
 * tab's cards are resolved from Algolia, one list per tab (aligned by index). */
export type ResolvedTabbedCollection = DereferencedTabbedCollection & {
  tabProducts: ShopCardPropsWithSlug[][];
};

const isTabbedCollection = (
  value: unknown,
): value is DereferencedTabbedCollection =>
  typeof value === "object" &&
  value !== null &&
  (value as { _type?: string })._type === "tabbedCollection" &&
  Array.isArray((value as { tabs?: unknown }).tabs);

/** Finds every Tabbed Collection within the given modules, whether it sits
 * directly in the array, comes from a Global Module, or is nested deeper
 * (e.g. inside a container module). */
const findTabbedCollections = (
  node: unknown,
  found: DereferencedTabbedCollection[] = [],
  seen: Set<unknown> = new Set(),
): DereferencedTabbedCollection[] => {
  if (typeof node !== "object" || node === null || seen.has(node)) {
    return found;
  }
  seen.add(node);

  if (isTabbedCollection(node)) {
    found.push(node);
    return found;
  }

  Object.values(node).forEach((child) =>
    findTabbedCollections(child, found, seen),
  );
  return found;
};

// Cache lives for the life of a build worker, so a Global Module shown on many
// pages only fetches the shared copy once per language.
const cardCopyCache = new Map<
  string,
  Promise<{ copy: ConversionCopy; shippingCopy: ShippingCopy }>
>();

const getCardCopy = (
  lang: string,
): Promise<{ copy: ConversionCopy; shippingCopy: ShippingCopy }> => {
  if (!cardCopyCache.has(lang)) {
    cardCopyCache.set(
      lang,
      Promise.all([
        sanityClient()
          .fetch<TranslationGroupSanityType>(
            buildTranslationGroupQuery(lang, "Conversion"),
          )
          .then((translations) =>
            toCopyObject<ConversionCopy>(lang, translations),
          ),
        sanityClient().fetch<ShippingCopy>(shippingCopyQuery),
      ]).then(([copy, shippingCopy]) => ({ copy, shippingCopy })),
    );
  }
  return cardCopyCache.get(lang);
};

/**
 * Prepares every Tabbed Collection in a page's modules for rendering. Call it
 * from `getStaticProps` with the module array(s) the page actually renders; it
 * mutates them in place.
 *
 * Pass only rendered modules, not the whole page document: a query's `...`
 * spread can also return stale fields that are no longer in the schema (e.g.
 * an old A/B test's modules), and walking those would do needless Algolia
 * lookups for content nobody sees.
 */
export async function resolveTabbedCollections(
  renderedModules: unknown,
  { lang, region }: Dialect,
): Promise<void> {
  const modules = findTabbedCollections(renderedModules);
  if (!modules.length) return;

  const { copy, shippingCopy } = await getCardCopy(lang);

  await Promise.all(
    modules.map(async (module) => {
      const tabProducts = await Promise.all(
        module.tabs.map(async (tab) => {
          if (!tab.shopCollection) {
            throw new Error(
              `[TabbedCollection] Tab "${
                tab.label?.[lang] ?? tab._key
              }" references an invalid or missing collection.`,
            );
          }

          const preview = await getCollectionPreview({
            collectionName: tab.shopCollection.name,
            collectionSlug: tab.shopCollection.slug,
            facetFilters: module.subscriptionOnly
              ? [["isSubscribable:true"]]
              : [],
            isPublic: true,
            lang,
            region,
            copy,
            shippingCopy,
            showShopcardDetail: Boolean(module.subscriptionOnly),
          });
          return preview.shopCards;
        }),
      );

      Object.assign(module, { tabProducts });
    }),
  );
}

/**
 * For Sanity's live preview only: `useLiveQuery` re-runs the page's GROQ
 * query on every edit and replaces the whole document with a fresh result,
 * which has no `tabProducts` — that field only ever gets added server-side,
 * by {@link resolveTabbedCollections}, during `getStaticProps`. Without this,
 * a page with a Tabbed Collection crashes in preview on every edit.
 *
 * Copies each Tabbed Collection's previously resolved `tabProducts` across by
 * `_key` so edits elsewhere on the page don't lose or blank out its products.
 * A module with no match in `resolvedModules` (e.g. just added in the
 * editor, so it was never built) falls back to empty tabs rather than
 * crashing; its products will appear once the page is rebuilt.
 *
 * Call this against the live modules before rendering them, not inside
 * `getStaticProps` — mutates `liveModules` in place.
 */
export function preserveTabbedCollectionProducts(
  liveModules: unknown,
  resolvedModules: unknown,
): void {
  const liveCollections = findTabbedCollections(liveModules);
  if (!liveCollections.length) return;

  const resolvedByKey = new Map(
    findTabbedCollections(resolvedModules)
      .filter(
        (module): module is ResolvedTabbedCollection =>
          module._key != null &&
          Array.isArray((module as ResolvedTabbedCollection).tabProducts),
      )
      .map((module) => [module._key, module as ResolvedTabbedCollection]),
  );

  liveCollections.forEach((module) => {
    const resolved = module._key ? resolvedByKey.get(module._key) : undefined;
    // Match per tab by _key, not by array position: a tab added, removed, or
    // reordered in the editor since the last build would otherwise pair this
    // tab with another tab's products.
    const resolvedProductsByTabKey = new Map(
      resolved?.tabs.map((tab, i) => [tab._key, resolved.tabProducts[i]]),
    );

    Object.assign(module, {
      tabProducts: module.tabs.map(
        (tab) => resolvedProductsByTabKey.get(tab._key) ?? [],
      ),
    });
  });
}
